import { threadBelongsToWorld } from "./neucleos/worldSelection";

export const ROOT_SESSION_STATUS_LABELS = {
  working: "Working",
  monitoring: "Monitoring",
  approval: "Approval needed",
  input: "Input needed",
  limited: "Limited",
  failed: "Failed",
  done: "New result",
} as const;

export type RootSessionStatus = keyof typeof ROOT_SESSION_STATUS_LABELS;

// Arc A1 (cockpit-evolution plan): the World-local root-session strip.
//
// The projection is pure and structural. Lifecycle fields arrive EFFECTIVE:
// the adapter resolves snooze/settle through the same client-runtime rules the
// sidebar uses and passes non-null values only when the state currently
// applies. The projection itself never re-derives lifecycle from clocks.
//
// Membership rule (PLAN.md, Arc A1): drafts + pinned live threads + active
// live threads + the currently addressed thread (deep-link exception for
// snoozed/settled) − archived − other Worlds. Tab selection is view state:
// nothing in this module creates, settles, snoozes, pins, archives, or
// branches anything.

export interface RootSessionStripThreadInput {
  readonly environmentId: string;
  readonly threadId: string;
  readonly worldKey: string | null;
  readonly sessionId?: string | null;
  readonly archivedAt: string | null;
  readonly pinnedAt: string | null;
  readonly snoozedUntil: string | null;
  readonly settledAt: string | null;
  // Arc A5 explicit parentage: null for a root, else the named parent.
  readonly parentThreadId: string | null;
  readonly title?: string | null;
  readonly status?: RootSessionStatus | null;
  /** When the latest turn completed (or the session failed), for the agent strip's Finished cards. */
  readonly finishedAt?: string | null;
}

export interface RootSessionStripDraftInput {
  readonly draftId: string;
  readonly environmentId: string;
  readonly threadId: string;
  readonly worldKey: string | null;
  readonly promotedTo: {
    readonly environmentId: string;
    readonly threadId: string;
  } | null;
  readonly title?: string | null;
}

export interface RootSessionAddress {
  readonly environmentId: string;
  readonly threadId: string;
}

export interface RootSessionTab {
  readonly key: string;
  readonly kind: "server" | "draft";
  readonly draftId: string | null;
  readonly environmentId: string;
  readonly threadId: string;
  readonly label: string;
  readonly status: RootSessionStatus | null;
  /** Arc A6: the tab is the fronted chain's node at its level (at most one per strip row). */
  readonly selected: boolean;
  /** Arc A5: the parent tab's key for a derived child position, null for a root/draft. */
  readonly parentKey: string | null;
  /** Arc A5: the live parent's label for announcement, null on roots/drafts/orphans. */
  readonly parentLabel: string | null;
  /** Arc A5: the named parent is not itself live — a typed orphan, never silently promoted to a root. */
  readonly orphan: boolean;
  /** Arc A6: the most urgent status among STRICT descendants — the computed
   * rollup that lets live work off the fronted chain surface once on its
   * visible ancestor. Display-only decoration; distinct from `status` (the
   * session's own answer) and recomputed per projection, never stored. */
  readonly descendantStatus: RootSessionStatus | null;
}

// Rollup urgency: a failure outranks a waiting human gate, which outranks
// requested input, which outranks running work, which outranks watching,
// which outranks an unseen completion.
const ROOT_SESSION_STATUS_URGENCY: Readonly<Record<RootSessionStatus, number>> = {
  failed: 6,
  approval: 5,
  input: 4,
  limited: 3,
  working: 2,
  monitoring: 1,
  done: 0,
};

export function moreUrgentRootSessionStatus(
  left: RootSessionStatus | null,
  right: RootSessionStatus | null,
): RootSessionStatus | null {
  if (left === null) return right;
  if (right === null) return left;
  return ROOT_SESSION_STATUS_URGENCY[left] >= ROOT_SESSION_STATUS_URGENCY[right] ? left : right;
}

// The one status→color mapping, shared by every surface that renders these
// statuses (session tabs, Session tabs, World doors): a second copy of this
// table is how two surfaces come to disagree about what amber means.
export function rootSessionStatusFillClass(status: RootSessionStatus): string {
  switch (status) {
    case "failed":
      return "bg-destructive";
    case "approval":
    case "input":
    case "limited":
      return "bg-warning";
    case "done":
      return "bg-neucleos-identity";
    case "working":
    case "monitoring":
      return "bg-cleo-identity";
  }
}

export function rootSessionStatusRingClass(status: RootSessionStatus): string {
  switch (status) {
    case "failed":
      return "border-destructive";
    case "approval":
    case "input":
    case "limited":
      return "border-warning";
    case "done":
      return "border-neucleos-identity";
    case "working":
    case "monitoring":
      return "border-cleo-identity";
  }
}

export function rootSessionStatusTextClass(status: RootSessionStatus): string {
  switch (status) {
    case "failed":
      return "text-destructive";
    case "approval":
    case "input":
    case "limited":
      return "text-warning";
    case "done":
      return "text-neucleos-identity";
    case "working":
    case "monitoring":
      return "text-cleo-identity";
  }
}

export interface RootSessionStripProjectionInput {
  readonly activeWorldKey: string | null;
  readonly availableWorldKeys: ReadonlyArray<string>;
  /** The explicit Local door: project only ungoverned work, whatever the membership count. */
  readonly local?: boolean;
  /** The ALL stance: the whole entitlement, before any World filter. */
  readonly all?: boolean;
  readonly threads: ReadonlyArray<RootSessionStripThreadInput>;
  readonly drafts: ReadonlyArray<RootSessionStripDraftInput>;
  readonly addressed: RootSessionAddress | null;
}

// Arc A6: the derived address chain. The leaf route stays the only shareable
// address; ancestors are derived by walking parent pointers through the flat
// records, and every way the walk can fail is a TYPED state — never a crash,
// never an invented root.
export type RootSessionAddressChain =
  | { readonly kind: "none" }
  | { readonly kind: "unavailable" }
  | { readonly kind: "grounded"; readonly keys: ReadonlyArray<string> }
  | {
      readonly kind: "orphaned";
      readonly keys: ReadonlyArray<string>;
      readonly defect: "missing-parent" | "cross-world-parent" | "cycle";
    };

/** Arc A6: one sibling strip. Level 0 is the root row; level k > 0 lists the
 * children of the fronted chain's node at level k − 1. */
export interface SessionStripRow {
  readonly level: number;
  readonly parentKey: string | null;
  readonly parentLabel: string | null;
  readonly tabs: ReadonlyArray<RootSessionTab>;
}

export interface RootSessionStripProjection {
  /** Typed whole-strip states: a multi-World scope without a valid selection
   * refuses projection; an empty tree and a fully parked tree are distinct
   * answers, not one generic blank. */
  readonly state: "world-unresolved" | "empty" | "all-parked" | "ready";
  readonly address: RootSessionAddressChain;
  readonly strips: ReadonlyArray<SessionStripRow>;
}

function sameIdentity(
  left: { readonly environmentId: string; readonly threadId: string },
  right: { readonly environmentId: string; readonly threadId: string },
): boolean {
  return left.environmentId === right.environmentId && left.threadId === right.threadId;
}

// The reserved environment/thread identity is the stable visual key: a draft
// keeps this exact key through promotion, so the tab neither remounts nor
// moves when its route target changes from /draft/… to /:env/:thread.
export function rootSessionTabKey(tab: {
  readonly environmentId: string;
  readonly threadId: string;
}): string {
  return `${tab.environmentId}:${tab.threadId}`;
}

// Arc A6 membership + recursion law (PLAN.md §Arc A6), built on the A5 laws:
//
// - Only parentless sessions occupy the root row. A child occupies exactly
//   one derived seat in the sibling strip of its level, and only while its
//   parent is on the fronted chain — flat records and parent pointers are the
//   sole inputs; nothing here persists a tree or reads one from the URL.
// - The addressed exception generalizes to the CHAIN exception: every node on
//   the fronted chain keeps its seat, parked or not, because fronting implies
//   visibility. Fronting still writes nothing (view-only law).
// - Anchor law, generalized from children to descendants: a parked root whose
//   subtree still holds qualifying work keeps its seat, so live work off the
//   chain stays noticeable. A fully parked family hides whole.
// - A child whose parent link is BROKEN (deleted, archived, other World) is a
//   typed orphan at the end of the root row — discoverable, marked, never
//   silently invented into a root.
export function projectRootSessionStrip(
  input: RootSessionStripProjectionInput,
): RootSessionStripProjection {
  const { activeWorldKey, availableWorldKeys, addressed } = input;
  const local = input.local === true;
  const all = input.all === true;

  // Governed scope with no valid active selection refuses at EVERY
  // membership count (O26): projecting anything would be a cross-World leak,
  // and a sole membership is not an implicit selection. The explicit Local
  // mode is a valid selection: it projects only ungoverned work. The ALL
  // stance is a valid selection too: the whole entitlement, unfiltered, is
  // never a leak — every thread shown is one the Actor is entitled to.
  if (!local && !all && (activeWorldKey === null || !availableWorldKeys.includes(activeWorldKey))) {
    return { state: "world-unresolved", address: { kind: "none" }, strips: [] };
  }

  const scope = { activeWorldKey, local, all };
  const isAddressed = (identity: RootSessionAddress) =>
    addressed !== null && sameIdentity(identity, addressed);

  const inWorldThreads = input.threads.filter((thread) =>
    threadBelongsToWorld(thread.worldKey, scope),
  );
  const liveThreads = inWorldThreads.filter((thread) => thread.archivedAt === null);
  const liveByKey = new Map(liveThreads.map((thread) => [rootSessionTabKey(thread), thread]));
  const parentKeyOf = (thread: RootSessionStripThreadInput): string | null => {
    const parentThreadId = thread.parentThreadId ?? null;
    return parentThreadId === null
      ? null
      : rootSessionTabKey({ ...thread, threadId: parentThreadId });
  };
  const childrenByParentKey = new Map<string, RootSessionStripThreadInput[]>();
  for (const thread of liveThreads) {
    const parentKey = parentKeyOf(thread);
    if (parentKey === null) continue;
    const group = childrenByParentKey.get(parentKey) ?? [];
    group.push(thread);
    childrenByParentKey.set(parentKey, group);
  }

  const liveIdentity = new Set(liveByKey.keys());
  const drafts = input.drafts.filter((draft) => {
    if (!threadBelongsToWorld(draft.worldKey, scope)) return false;
    // A promoted draft's tab is its server thread; projecting both would
    // duplicate one session. The reserved identity is the join key.
    if (draft.promotedTo !== null) return false;
    if (liveIdentity.has(rootSessionTabKey(draft))) return false;
    return true;
  });

  // --- Derive the address chain by walking parent pointers upward. ---------
  const addressedThread =
    addressed === null ? undefined : liveByKey.get(rootSessionTabKey(addressed));
  const addressedDraft =
    addressed === null ? undefined : drafts.find((draft) => sameIdentity(draft, addressed));

  let address: RootSessionAddressChain;
  let chainThreads: ReadonlyArray<RootSessionStripThreadInput> = [];
  if (addressed === null) {
    address = { kind: "none" };
  } else if (addressedDraft !== undefined) {
    // A draft is a parentless leaf-to-be; its chain is itself.
    address = { kind: "grounded", keys: [rootSessionTabKey(addressedDraft)] };
  } else if (addressedThread === undefined) {
    // Deleted, archived, or never-existed leaf: typed absence. The archived
    // law holds absolutely — no exception resurrects it, no strip is invented.
    address = { kind: "unavailable" };
  } else {
    const upward: RootSessionStripThreadInput[] = [addressedThread];
    const visited = new Set([rootSessionTabKey(addressedThread)]);
    let cursor = addressedThread;
    let defect: "missing-parent" | "cross-world-parent" | "cycle" | null = null;
    for (;;) {
      const parentKey = parentKeyOf(cursor);
      if (parentKey === null) break;
      if (visited.has(parentKey)) {
        defect = "cycle";
        break;
      }
      const parent = liveByKey.get(parentKey);
      if (parent === undefined) {
        // Type the broken edge: a live parent in another World is a
        // cross-World edge; anything else (absent or archived) is no longer
        // an accountable session to point at — missing-parent, verbatim from
        // the A5 refusal vocabulary.
        const raw = input.threads.find(
          (thread) =>
            thread.environmentId === cursor.environmentId &&
            thread.threadId === (cursor.parentThreadId ?? null) &&
            thread.archivedAt === null,
        );
        defect = raw !== undefined ? "cross-world-parent" : "missing-parent";
        break;
      }
      upward.push(parent);
      visited.add(parentKey);
      cursor = parent;
    }
    chainThreads = upward.toReversed();
    const keys = chainThreads.map((thread) => rootSessionTabKey(thread));
    address = defect === null ? { kind: "grounded", keys } : { kind: "orphaned", keys, defect };
  }
  const chainKeys = new Set(
    address.kind === "grounded" || address.kind === "orphaned" ? address.keys : [],
  );

  // --- Qualification: who keeps a seat. ------------------------------------
  const parked = (thread: RootSessionStripThreadInput) =>
    thread.snoozedUntil !== null || thread.settledAt !== null;
  const seatedInOwnRight = (thread: RootSessionStripThreadInput) =>
    !parked(thread) || isAddressed(thread) || chainKeys.has(rootSessionTabKey(thread));
  const hasQualifyingDescendant = (thread: RootSessionStripThreadInput): boolean => {
    const stack = [...(childrenByParentKey.get(rootSessionTabKey(thread)) ?? [])];
    const visited = new Set([rootSessionTabKey(thread)]);
    while (stack.length > 0) {
      const next = stack.pop()!;
      const nextKey = rootSessionTabKey(next);
      if (visited.has(nextKey)) continue;
      visited.add(nextKey);
      if (seatedInOwnRight(next)) return true;
      stack.push(...(childrenByParentKey.get(nextKey) ?? []));
    }
    return false;
  };
  const qualifies = (thread: RootSessionStripThreadInput) =>
    seatedInOwnRight(thread) || hasQualifyingDescendant(thread);

  // Arc A6 rollup: fold the STRICT descendants' own statuses into one
  // most-urgent answer per session, visiting each descendant exactly once —
  // the visited set both enforces "rolls up once" and terminates corrupt
  // parent-pointer loops with a computed value instead of a hang.
  const descendantStatusOf = (thread: RootSessionStripThreadInput): RootSessionStatus | null => {
    let rollup: RootSessionStatus | null = null;
    const visited = new Set([rootSessionTabKey(thread)]);
    const stack = [...(childrenByParentKey.get(rootSessionTabKey(thread)) ?? [])];
    while (stack.length > 0) {
      const next = stack.pop()!;
      const nextKey = rootSessionTabKey(next);
      if (visited.has(nextKey)) continue;
      visited.add(nextKey);
      rollup = moreUrgentRootSessionStatus(rollup, next.status ?? null);
      stack.push(...(childrenByParentKey.get(nextKey) ?? []));
    }
    return rollup;
  };

  const toServerTab = (
    thread: RootSessionStripThreadInput,
    parentLabel: string | null = null,
    orphan = false,
  ): RootSessionTab => ({
    key: rootSessionTabKey(thread),
    kind: "server",
    draftId: null,
    environmentId: thread.environmentId,
    threadId: thread.threadId,
    label: thread.title ?? thread.threadId,
    status: thread.status ?? null,
    selected: chainKeys.has(rootSessionTabKey(thread)),
    parentKey: parentKeyOf(thread),
    parentLabel,
    orphan,
    descendantStatus: descendantStatusOf(thread),
  });

  // Row ordering: pinned live seats first, then active, then parked-but-
  // qualifying seats (chain/anchor exceptions) at the end of the row.
  const orderRow = (threads: ReadonlyArray<RootSessionStripThreadInput>) => {
    const qualifying = threads.filter(qualifies);
    return [
      ...qualifying.filter((thread) => !parked(thread) && thread.pinnedAt !== null),
      ...qualifying.filter((thread) => !parked(thread) && thread.pinnedAt === null),
      ...qualifying.filter(parked),
    ];
  };

  // --- Row 0: roots + drafts + typed orphan seats. -------------------------
  const rootTabs = orderRow(
    liveThreads.filter((thread) => (thread.parentThreadId ?? null) === null),
  ).map((thread) => toServerTab(thread));

  const draftTabs = drafts.map(
    (draft): RootSessionTab => ({
      key: rootSessionTabKey(draft),
      kind: "draft",
      draftId: draft.draftId,
      environmentId: draft.environmentId,
      threadId: draft.threadId,
      label: draft.title ?? "New session",
      status: null,
      selected: isAddressed(draft),
      parentKey: null,
      parentLabel: null,
      orphan: false,
      descendantStatus: null,
    }),
  );

  // Broken parent link ⇒ typed orphan seat. A corrupt cycle's fronted top is
  // seated the same way (its parent "exists" inside the loop, so only the
  // chain walk can witness the defect).
  const brokenLink = liveThreads.filter((thread) => {
    const parentKey = parentKeyOf(thread);
    return parentKey !== null && !liveByKey.has(parentKey);
  });
  const orphanThreads = [...brokenLink];
  if (address.kind === "orphaned" && address.defect === "cycle" && chainThreads.length > 0) {
    const top = chainThreads[0]!;
    if (!orphanThreads.some((thread) => rootSessionTabKey(thread) === rootSessionTabKey(top))) {
      orphanThreads.push(top);
    }
  }
  const orphanTabs = orderRow(orphanThreads).map((thread) => ({
    ...toServerTab(thread),
    orphan: true,
  }));

  const strips: SessionStripRow[] = [];
  const rowZeroTabs = [...rootTabs, ...draftTabs, ...orphanTabs];
  if (rowZeroTabs.length > 0) {
    strips.push({ level: 0, parentKey: null, parentLabel: null, tabs: rowZeroTabs });
  }

  // --- Rows k ≥ 1: one sibling strip per fronted chain level. --------------
  // The seated set enforces the single-seat law mechanically: a session that
  // already holds a seat (root, draft, orphan, or an earlier level) can never
  // be dealt a second one — which is also what keeps a corrupt cycle's loop
  // from re-rendering its members forever.
  const seatedKeys = new Set(rowZeroTabs.map((tab) => tab.key));
  for (const [index, chainNode] of chainThreads.entries()) {
    const parentKey = rootSessionTabKey(chainNode);
    const parentLabel = chainNode.title ?? chainNode.threadId;
    const childTabs = orderRow(
      (childrenByParentKey.get(parentKey) ?? []).filter(
        (thread) => !seatedKeys.has(rootSessionTabKey(thread)),
      ),
    ).map((thread) => toServerTab(thread, parentLabel));
    if (childTabs.length === 0) continue;
    for (const tab of childTabs) seatedKeys.add(tab.key);
    strips.push({ level: index + 1, parentKey, parentLabel, tabs: childTabs });
  }

  const state: RootSessionStripProjection["state"] =
    strips.length > 0 ? "ready" : liveThreads.length > 0 ? "all-parked" : "empty";
  return { state, address, strips };
}

// RM-CAN-14 (2026-09-20): the rendered strip (`RootSessionTabStrip`,
// `SessionStripStack`) is deleted. Local's rows stand on the Room Rail
// (components/RailNavigationRows.tsx through RoomRailLocalOrgans.tsx). What
// remains here is what other seats still read: the status vocabulary and the
// projection the Local frame derives its room boundary from.
